// --------------------------------------------------------------
// Created On: 2025-04-08
// Author: Hannah Vaughan
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import styles from "./ValvePositionControls.module.scss";
import PanelSection from "../PanelSection/PanelSection";

// Container for the valve position control buttons and manual input field for all 4 possible valves.
export default function ValvePositionControls(props: Props): Component {
  const [manualValvePosition1, setManualValvePosition1] = useState<string>("");
  const [manualValvePosition2, setManualValvePosition2] = useState<string>("");
  const [manualValvePosition3, setManualValvePosition3] = useState<string>("");
  const [manualValvePosition4, setManualValvePosition4] = useState<string>("");

  // Gets the manual valve position input for the provided valve.
  function getManualValvePosition(valveNumber: number): string {
    switch (valveNumber) {
      case 1:
        return manualValvePosition1;
      case 2:
        return manualValvePosition2;
      case 3:
        return manualValvePosition3;
      case 4:
        return manualValvePosition4;
      default:
        return "";
    }
  }

  // Updates the manual valve position input for the provided valve.
  function updateManualValvePosition(valveNumber: number, percentOpen: string): void {
    switch (valveNumber) {
      case 1:
        setManualValvePosition1(percentOpen);
        break;
      case 2:
        setManualValvePosition2(percentOpen);
        break;
      case 3:
        setManualValvePosition3(percentOpen);
        break;
      case 4:
        setManualValvePosition4(percentOpen);
        break;
      default:
        break;
    }
  }

  // Parses the manual valve position input and uses it to change the valve position.
  function applyManualValvePosition(valveNumber: number, percentOpen: string): void {
    percentOpen = percentOpen.trim();
    if (percentOpen.length === 0) {
      props.onError("Invalid percent entered.");
    } else {
      const percentOpenNumber = parseInt(percentOpen, 10);
      if (Number.isNaN(percentOpenNumber)) {
        props.onError("Entered percent is not an integer.");
      } else if (percentOpenNumber < 0) {
        props.onError("Entered percent must be a positive number.");
      } else if (percentOpenNumber > 100) {
        props.onError("Entered percent must not exceed 100.");
      } else {
        props.onChangeValvePosition(valveNumber, percentOpenNumber);
      }
    }
  }

  return (
    <Fragment>
      {props.valves.map((valve) => (
        <PanelSection key={valve} label={`Valve #${valve} Position`}>
          {props.positions.map((position) => (
            <div key={position} className={`col-6 col-lg-3 px-3 ${styles.controlColumn}`}>
              <button
                className="btn btn-primary w-100"
                type="button"
                disabled={props.disabled}
                onClick={() => props.onChangeValvePosition(valve, position)}
              >
                {position === 0 ? <span>Close</span> : <span>{position}% Open</span>}
              </button>
            </div>
          ))}

          <div className="col-8 g-0 ps-3">
            <input
              className={`${styles.valvePosition} align-self-center`}
              type="text"
              maxLength={10}
              value={getManualValvePosition(valve)}
              onChange={(e) => updateManualValvePosition(valve, e.target.value)}
            />
          </div>
          <div className={`col-4 g-0 pe-3 ${styles.valvePositionButtonColumn}`}>
            <button
              className={`${styles.valvePositionButton} align-self-center btn-primary`}
              type="button"
              disabled={props.disabled}
              aria-label="Set Percent Open"
              onClick={() => applyManualValvePosition(valve, getManualValvePosition(valve))}
            >
              {/* The short label is swapped in by a container query when the button column is narrow. */}
              <span className={styles.longLabel}>Set Percent Open</span>
              <span className={styles.shortLabel}>Set %</span>
            </button>
          </div>
        </PanelSection>
      ))}
    </Fragment>
  );
}

interface Props {
  valves: number[];
  positions: number[];
  disabled: boolean;
  onChangeValvePosition: (valve: number, position: number) => void;
  onError: (error: string) => void;
}
